import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_ADD_STEP_PHASES,
  CAMPAIGN_STEP_DELAYS,
} from '../model/use-campaign-add-step'
import type { UseCampaignAddStepResult } from '../model/use-campaign-add-step'
import { useCampaignSequence } from '../model/use-campaign-sequence'
import { useCampaignStepPreview } from '../model/use-campaign-step-preview'
import type { UseCampaignSequenceResult } from '../model/use-campaign-sequence'
import { CampaignAddStepPicker } from './campaign-add-step-picker'
import { CampaignStepComposer } from './campaign-step-composer'
import { CampaignSequenceRail } from './campaign-sequence-rail'
import {
  CampaignSequenceRailSkeleton,
  CampaignStepsSkeleton,
} from './campaign-skeletons'
import { CampaignStepCard } from './campaign-step-card'
import { CampaignWaitConnector } from './campaign-wait-connector'

interface CampaignSequenceTabProps {
  campaignId: string
  /** Owned by the detail page, so the header's "Add step" can open it. */
  addStep: UseCampaignAddStepResult
}

/**
 * The saved steps. A step being edited swaps its card for a composer opened on
 * that step's own content — steps are append-only, so an edit is a change to
 * one card rather than anything that moves the sequence.
 */
function StepList({
  sequence,
  threadSubject,
}: {
  sequence: UseCampaignSequenceResult
  threadSubject: string | undefined
}) {
  const { t } = useTranslation()
  return (
    <>
      {sequence.steps.map((step, index) => (
        <Fragment key={step.id}>
          {sequence.editingStepId === step.id ? (
            <CampaignStepComposer
              channel={step.channel}
              position={step.position}
              timing={step.timing}
              seed={sequence.seedFor(step.id)}
              saving={sequence.isSavingStep}
              title={t('campaigns.composer.editTitle', {
                position: step.position,
              })}
              saveLabel={t('campaigns.composer.saveChanges')}
              threadSubject={threadSubject}
              onCancel={sequence.onCancelEdit}
              onSave={(draft) => sequence.onSaveStep(step.id, draft)}
            />
          ) : (
            <CampaignStepCard
              step={step}
              actions={{
                onEdit: () => sequence.onEditStep(step.id),
                onToggleActive: (isActive) =>
                  sequence.onToggleActive(step.id, isActive),
                onMove: (position) => sequence.onMoveStep(step.id, position),
                canMoveUp: index > 0,
                canMoveDown: index < sequence.steps.length - 1,
                onDelete: () => sequence.onDeleteStep(step.id),
                canDelete: sequence.canDeleteSteps,
              }}
              onBodyChange={(body) => sequence.onBodyChange(step.id, body)}
            />
          )}
          <CampaignWaitConnector wait={step.wait} />
        </Fragment>
      ))}
    </>
  )
}

export function CampaignSequenceTab({
  campaignId,
  addStep,
}: CampaignSequenceTabProps) {
  const { t } = useTranslation()
  const sequence = useCampaignSequence(campaignId)
  // Only while the picker is open: a dry run costs a round trip and its
  // eligible count is meaningless anywhere else on the tab.
  const preview = useCampaignStepPreview({
    campaignId,
    enabled: addStep.phase === CAMPAIGN_ADD_STEP_PHASES.picking,
    channel: addStep.channel,
    delayMinutes: addStep.delayMinutes,
    conditions: addStep.conditions,
  })
  // What the picker settled on, read back as the sentence the composer heads
  // with — the same words, so nothing looks renegotiated between the two.
  const timing = [
    addStep.delay === CAMPAIGN_STEP_DELAYS.custom
      ? t('campaigns.composer.waitDays', { count: Number(addStep.customDays) })
      : t(`campaigns.sequence.addStep.delay.${addStep.delay}`),
    addStep.alwaysSend
      ? t('campaigns.sequence.addStep.alwaysSend')
      : t('campaigns.composer.onlyIf', {
          conditions: addStep.conditions
            .map((condition) =>
              t(`campaigns.sequence.addStep.condition.${condition}`),
            )
            .join(', '),
        }),
  ].join(' · ')
  // Step 1's subject is what a "reply in thread" step inherits.
  const threadSubject = sequence.steps.find(
    (step) => step.subject != null,
  )?.subject
  return (
    <div className="flex flex-col gap-4 xl:flex-row">
      <div className="flex min-w-0 flex-1 flex-col">
        {sequence.isLoading && <CampaignStepsSkeleton />}
        <StepList sequence={sequence} threadSubject={threadSubject} />
        {/* The column has no gap of its own: every step, the last included,
            closes with a wait connector, and that supplies the rhythm down to
            the add row as well. */}
        <div>
          <CampaignAddStepPicker state={addStep} summary={preview.summary} />
          {addStep.phase === CAMPAIGN_ADD_STEP_PHASES.composing && (
            <CampaignStepComposer
              channel={addStep.channel}
              position={sequence.steps.length + 1}
              timing={timing}
              saving={sequence.isAddingStep}
              threadSubject={threadSubject}
              onCancel={addStep.onBackToPicker}
              onSave={(draft) => {
                const sent = sequence.onAddStep(draft, {
                  delayMinutes: addStep.delayMinutes,
                  conditions: addStep.conditions,
                })
                if (sent) addStep.onClose()
              }}
            />
          )}
        </div>
      </div>
      <div className="w-full shrink-0 xl:w-90">
        {sequence.rail == null ? (
          <CampaignSequenceRailSkeleton />
        ) : (
          <CampaignSequenceRail
            rail={sequence.rail}
            onToggle={sequence.onToggleSwitch}
            busy={sequence.isSavingSwitch}
          />
        )}
      </div>
    </div>
  )
}
